import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, Users, CheckCircle2, Clock, Heart, Bookmark } from 'lucide-react';
import type { ContentKPIs } from '@/types/content';

// SPEC-021: Six KPI tiles for the content metrics page. Presentation-only;
// the parent page owns the query + loading state.

interface ContentKpiCardsProps {
  kpis: ContentKPIs;
}

export function ContentKpiCards({ kpis }: ContentKpiCardsProps) {
  const tiles = [
    {
      label: 'Total views',
      value: kpis.total_views.toLocaleString(),
      icon: Eye,
    },
    {
      label: 'Unique viewers',
      value: kpis.unique_viewers.toLocaleString(),
      icon: Users,
    },
    {
      label: 'Completion rate',
      value: `${Math.round(kpis.completion_rate * 100)}%`,
      sub: `${kpis.completed_views} completed`,
      icon: CheckCircle2,
    },
    {
      label: 'Avg time',
      value: formatSeconds(kpis.avg_time_seconds),
      icon: Clock,
    },
    {
      label: 'Likes',
      value: kpis.like_count.toLocaleString(),
      icon: Heart,
    },
    {
      label: 'Bookmarks',
      value: kpis.bookmark_count.toLocaleString(),
      icon: Bookmark,
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {tiles.map((t) => (
        <Card key={t.label}>
          <CardHeader className="flex flex-row items-center justify-between pb-1">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t.label}
            </CardTitle>
            <t.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{t.value}</p>
            {t.sub && <p className="mt-0.5 text-xs text-muted-foreground">{t.sub}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function formatSeconds(sec: number): string {
  if (!sec || sec < 1) return '0s';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}
